/* eslint-disable react/display-name */
import React, { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { Button } from "@chakra-ui/react"
import { useHistory } from "react-router-dom"
import dayjs from "dayjs"
import { Table } from "antd"
import _ from "lodash"
import pdfMake from "addthaifont-pdfmake/build/pdfmake"
import "addthaifont-pdfmake/build/vfs_fonts"

import {
  Alert,
  GeneralMainLayout as Layout,
  ReportDateSelector,
  CSVGeneratorPanel,
} from "../../components"
import * as actions from "../../redux/actions"
import { dayAmount, arrayBookingAnalysis } from "../../util"
import { appConfig } from "../../config"
import { BookingReport as BookingReportPDF } from "../../config/pdfmake/template"
import api from "../../config/api"

function IncomingReport() {
  const { t } = useTranslation()
  const history = useHistory()
  const [isError, setIsError] = useState(false)
  const [errMessage, setErrMessage] = useState("")
  const [date, setDate] = useState(new Date())
  const [startDate, setStartDate] = useState()
  const [endDate, setEndDate] = useState()
  const [page, setPage] = useState(1)
  const size = 10
  const [isLoading, setIsLoading] = useState(false)
  const [orderByField, setOrderByField] = useState("")
  const [orderBy, setOrderBy] = useState("")
  const [excelExportOpen, setExcelExportOpen] = useState(false)

  const booking = useSelector((state) => state.bookings)
  const information = useSelector((state) => state.information)
  const system = useSelector((state) => state.system)
  const dispatch = useDispatch()

  useEffect(() => {
    setExcelExportOpen(false)
    dispatch(
      actions.getAllBookingAndLog({
        checkin: date ? dayjs(date).format("YYYY-MM-DD") : "",
        page,
        size,
        orderBy,
        orderByField,
      }),
    )
      .then(() => {
        setIsLoading(true)
      })
      .catch((err) => {
        setIsError(true)
        setErrMessage(err?.message)
      })

    return () => {}
  }, [date, page, orderBy, orderByField, startDate, endDate])

  const columns = [
    {
      title: "ลำดับที่",
      index: true,
      dataIndex: "index",
      render: (text, record, index) => index + 1,
    },
    {
      title: "เลขที่กาารจอง",
      dataIndex: "booking_no",
      render: (text, record) => record?.booking_no,
      sorter: true,
    },
    {
      title: "ลูกค้า",
      dataIndex: "customer",
      render: (text, record) => `  ${record?.customer?.firstname} ${record?.customer?.lastname}`,
    },
    {
      title: "โทร",
      dataIndex: "customer",
      render: (text, record) => record?.customer?.tel,
    },

    {
      title: "วันที่เข้าพัก",
      dataIndex: "bookings",
      render: (text, record) => `${dayjs(record?.start).format("dd D/MM/YY")}`,
    },
    {
      title: "วันที่ออก",
      dataIndex: "bookings",
      render: (text, record) => `${dayjs(record?.end).format("dd D/MM/YY")}`,
    },
    {
      title: "ห้องพัก",
      dataIndex: "list",
      render: (text, record) => _.map(record?.list, (eachList) => `${eachList?.room?.name} `),
    },
    {
      title: "ยังไม่ระบุห้อง",
      dataIndex: "no_room_booking",
      render: (text, record) =>
        _.map(
          record?.no_room_booking,
          (eachList) => `${eachList?.roomtype?.translation?.th?.name} x ${eachList?.amount} `,
        ),
    },
    {
      title: "จำนวนคืน",
      dataIndex: "bookings",
      render: (text, record) => `${dayAmount(record?.start, record?.end)} คืน`,
    },
    {
      title: "ราคา",
      dataIndex: "priceBeforeDiscount",
      render: (text, record) => record?.priceBeforeDiscount,
    },

    {
      title: "ส่วนลด",
      dataIndex: "total_discount",
      render: (text, record) => record?.total_discount,
    },
    {
      title: "ราคาสุทธิ",
      dataIndex: "price",
      render: (text, record) => record?.price,
    },
    {
      title: "มัดจำ",
      dataIndex: "payment.paid_amount",
      render: (text, record) => record?.payment?.paid_amount,
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      sorter: true,
      render: (text, record) => record?.status,
    },
    {
      title: "บริการ",
      dataIndex: "services",
      render: (text, record) =>
        _.map(record?.services, (each) => `${each?.service?.name} x ${each?.amount} `),
    },
    {
      title: "หมายเหตุ",
      dataIndex: "bookings",
      render: (text, record) => record?.bookings?.remark,
    },
  ]

  const onChange = (pagination, filters, sorter, extra) => {
    const newPage = pagination?.current
    setPage(newPage)
    console.log("params", pagination, filters, sorter, extra)
    if (sorter?.order === "ascend") {
      setOrderBy("asc")
    } else {
      setOrderBy("desc")
    }

    if (sorter?.field === "booking_no") {
      setOrderByField("booking_no")
    }

    if (sorter?.field === "status") {
      setOrderByField("status")
    }
  }

  const fullQueryParams = new URLSearchParams({
    page: 1,
    size: appConfig.maxFetchSize,
    checkin: date ? dayjs(date).format("YYYY-MM-DD") : "",
  })

  const handlePrintPDF = async () => {
    try {
      setIsLoading(false)
      api
        .get(`${process.env.REACT_APP_API_URL}/booking/with-logs/?${fullQueryParams.toString()}`)
        .then((res) => {
          const pdfDeginition = BookingReportPDF(
            arrayBookingAnalysis(res?.data?.rows),
            date,
            date !== null,
            startDate,
            endDate,
            information,
            system,
          )
          console.log("PDF Definition", pdfDeginition)
          pdfMake.createPdf(pdfDeginition).open()
        })
        .catch((error) => {
          alert(error?.message)
        })
        .finally(() => {
          setIsLoading(true)
        })
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Layout categoryTitle={t("history.title")} title="รายงานแขกเข้าพักในแต่ละวัน">
      <Alert
        errMessage={errMessage}
        isError={isError}
        handleOnClose={() => {
          setIsError(false)
          setErrMessage("")
        }}
      />
      <div className="flex justify-between px-4">
        <Button
          colorScheme="purple"
          variant="outline"
          onClick={() => {
            history.goBack()
          }}
        >
          {t("general.back")}
        </Button>
        <div className="flex gap-2">
          <Button
            colorScheme="red"
            size="sm"
            leftIcon={<i className="fas fa-file-pdf"></i>}
            onClick={() => handlePrintPDF()}
          >
            PDF
          </Button>
          <Button
            colorScheme="green"
            size="sm"
            leftIcon={<i className="fas fa-file-excel"></i>}
            onClick={() => {
              if (excelExportOpen === true) {
                setExcelExportOpen(false)
                setTimeout(() => {
                  setExcelExportOpen(true)
                }, 500)
              } else {
                setExcelExportOpen(true)
              }
            }}
          >
            CSV
          </Button>
        </div>
      </div>
      <div className="p-4 w-full">
        <ReportDateSelector pointDate={date} setPointDate={setDate} dateOnly />
        <CSVGeneratorPanel
          apiSuffix="booking/with-logs"
          columns={columns}
          open={excelExportOpen}
          queryParams={fullQueryParams}
        />
        {isLoading && (
          <Table
            columns={columns}
            dataSource={booking?.arr}
            onChange={onChange}
            pagination={{
              current: page,
              pageSize: size,
              total: booking?.total,
            }}
          />
        )}
      </div>
    </Layout>
  )
}

export default IncomingReport
